/**
 * The till app around this till, when there is one (apps/till-app).
 *
 * On an Android tablet set up as a till, this page runs inside the app's web
 * view, at its real address, and the app offers it `window.tillNative`: what
 * a browser will not do for a page. Printers over USB and Bluetooth, raw
 * bytes to any receipt printer on the network, plain HTTP to an Epson or Star
 * without accepting its certificate, Android's printing, and keeping the
 * tablet locked to the till. In a browser there is no `tillNative` and the
 * till works exactly as it does without the app.
 *
 * Each call is a message `{id, method, params}`; the app answers
 * `{id, ok, result}` or `{id, ok: false, error}`, the error already in words
 * for the person at the till.
 */

interface TillNativePort {
  postMessage(message: string): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
}

declare global {
  interface Window {
    tillNative?: TillNativePort;
  }
}

/** What the app says about itself and the tablet. */
export interface TillAppInfo {
  version: string;
  /** The till's address this tablet opens. */
  server: string;
  /** Set up as a dedicated till: locks itself in, starts with the tablet. */
  dedicated: boolean;
  /** Locked to the till right now. */
  locked: boolean;
  usb: boolean;
  bluetooth: boolean;
}

/** A printer the tablet can see: a USB one by maker and model, a Bluetooth one by address. */
export interface FoundPrinter {
  id: string;
  name: string;
}

export type RawTarget =
  | { kind: 'network'; host: string; port: number }
  | { kind: 'usb'; id: string }
  | { kind: 'bluetooth'; id: string };

/** The app's answer when it could not do something, in words for the till. */
export class TillAppError extends Error {}

function port(): TillNativePort | undefined {
  return typeof window === 'undefined' ? undefined : window.tillNative;
}

/** Is this till running inside the till app? */
export function isTillApp(): boolean {
  return port() !== undefined;
}

type Waiting = { resolve: (value: unknown) => void; reject: (error: Error) => void };
const waiting = new Map<string, Waiting>();
let nextId = 0;
let listening = false;

function listen(native: TillNativePort) {
  if (listening) return;
  listening = true;
  native.addEventListener('message', (event) => {
    let answer: { id?: string; ok?: boolean; result?: unknown; error?: string };
    try {
      answer = JSON.parse(String(event.data)) as typeof answer;
    } catch {
      return;
    }
    const call = answer.id ? waiting.get(answer.id) : undefined;
    if (!call || !answer.id) return;
    waiting.delete(answer.id);
    if (answer.ok) call.resolve(answer.result);
    else call.reject(new TillAppError(answer.error ?? 'The till app could not do that.'));
  });
}

function call<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  const native = port();
  if (!native) return Promise.reject(new TillAppError('This only works in the till app.'));
  listen(native);
  nextId += 1;
  const id = String(nextId);
  return new Promise<T>((resolve, reject) => {
    waiting.set(id, { resolve: resolve as (value: unknown) => void, reject });
    native.postMessage(JSON.stringify({ id, method, params }));
  });
}

/** Bytes as base64, in slices: one spread of a long receipt's bytes overflows the stack. */
function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export const tillApp = {
  info: () => call<TillAppInfo>('info'),

  /** This page through Android's printing; its print styles decide what comes out. */
  printPage: () => call<null>('print.page'),
  /** A page of its own through Android's printing. */
  printHtml: (html: string) => call<null>('print.html', { html }),

  /** An Epson or Star request, plain HTTP allowed, to a printer on the local network. */
  postToPrinter: (
    url: string,
    headers: Record<string, string>,
    body: string,
    timeoutMs = 12_000,
  ) =>
    call<{ status: number; body: string }>('printer.http', { url, headers, body, timeoutMs }),
  /** ESC/POS bytes to a printer on the network, USB or Bluetooth. */
  sendRaw: (target: RawTarget, bytes: Uint8Array) =>
    call<null>('printer.raw', { target, data: toBase64(bytes) }),
  usbPrinters: () => call<FoundPrinter[]>('printer.usb.list'),
  bluetoothPrinters: () => call<FoundPrinter[]>('printer.bluetooth.list'),

  lock: () => call<TillAppInfo>('kiosk.lock'),
  unlock: () => call<TillAppInfo>('kiosk.unlock'),
  /** Stop being a dedicated till, so the app can be removed. */
  release: () => call<TillAppInfo>('kiosk.release'),
  /** The app's own screen for which till this tablet opens. */
  changeServer: () => call<null>('server.change'),
};

/**
 * In the app, `window.print()` goes to Android's printing: a web view has no
 * print window of its own, and the till prints the paper on screen with it
 * (receipt.tsx). Before the first render, so every caller gets it.
 */
export function startTillApp(): void {
  if (!isTillApp()) return;
  window.print = () => {
    void tillApp.printPage().catch(() => undefined);
  };
}
